'use client';

import { useAuthContext } from '@lib/context/AuthContext';
import { ProfileImage } from '@components/ui/ProfileImage';
import Link from 'next/link';
import { memo, useState, useRef, useEffect } from 'react';

export const Navbar = memo(function Navbar() {
  const { user, status, signOut } = useAuthContext();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const isLoading = status === 'loading';
  const isAuthenticated = !!user;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }

    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isProfileOpen) setIsProfileOpen(false);
  };

  return (
    <nav className="sticky top-0 z-10 w-full py-3 sm:py-4 px-3 sm:px-6 border-b border-gray-200 bg-white shadow-sm backdrop-blur-lg bg-white/90">
      <div className="container mx-auto flex justify-between items-center">
        <Link 
          href="/" 
          className="text-lg sm:text-xl font-bold tracking-tight text-black transition-colors hover:opacity-80 flex items-center"
        >
          <span className="bg-blue-600 text-white w-7 h-7 sm:w-8 sm:h-8 rounded-md flex items-center justify-center mr-2 font-mono">ST</span>
          <span className="hidden xs:inline">Saggy Tits Leaderboard</span>
          <span className="inline xs:hidden">STL</span>
        </Link>
        
        {/* Mobile menu button */}
        {isAuthenticated && (
          <button 
            className="md:hidden flex items-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={toggleMobileMenu}
            aria-expanded={isMobileMenuOpen}
            aria-label="Toggle mobile menu"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        )}
        
        <div className="flex items-center space-x-2 sm:space-x-4">
          {isAuthenticated && (
            <div className="hidden md:flex items-center space-x-6 mr-4">
              <Link
                href="/leaderboard"
                className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
              >
                Leaderboard
              </Link>
              <Link
                href="/lists"
                className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
              >
                Lists
              </Link>
              <Link
                href="/good-boy-points"
                className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
              >
                Good Boy Points
              </Link>
              <Link
                href="/photos"
                className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
              >
                Photos
              </Link>
              {/* Users link should always be the rightmost item in desktop navigation */}
              <Link
                href="/users"
                className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
              >
                Users
              </Link>
            </div>
          )}
          
          {isLoading ? (
            <div className="w-20 h-8 sm:w-24 sm:h-10 bg-gray-200 rounded animate-pulse" />
          ) : isAuthenticated ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-1 sm:space-x-2 focus:outline-none"
                aria-expanded={isProfileOpen}
                aria-haspopup="true"
              >
                {user.image ? (
                  <ProfileImage 
                    src={user.image} 
                    alt={user.name || user.email || 'User'} 
                    size={user.name ? 40 : 32}
                    className="ring-2 ring-gray-200"
                  />
                ) : (
                  <ProfileImage 
                    src={null} 
                    alt={user.name || user.email || 'User'} 
                    size={user.name ? 40 : 32}
                    className="ring-2 ring-gray-200"
                  />
                )}
                <span className="hidden md:block text-sm font-medium text-gray-700">
                  {user.name?.split(' ')[0] || 'User'}
                </span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown menu */}
              {isProfileOpen && (
                <div 
                  className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 ring-1 ring-gray-200 animate-fade-in z-50"
                  onClick={() => setIsProfileOpen(false)}
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {user.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 overflow-ellipsis overflow-hidden break-all">
                      {user.email}
                    </p>
                  </div>
                  <Link 
                    href="/profile" 
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Your Profile
                  </Link>
                  <Link 
                    href="/settings" 
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Settings
                  </Link>
                  <button
                    onClick={() => signOut()}
                    className="w-full text-left block px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              href="/auth/login"
              className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              aria-label="Sign in"
            >
              Sign In
            </Link>
          )}
        </div>
      </div>
      
      {/* Mobile menu, show/hide based on menu state */}
      {isAuthenticated && isMobileMenuOpen && (
        <div className="md:hidden pt-2 pb-3 border-t border-gray-200 mt-3 animate-fade-in">
          <div className="space-y-1 px-2">
            <Link
              href="/leaderboard"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Leaderboard
            </Link>
            <Link
              href="/lists"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Lists
            </Link>
            <Link
              href="/good-boy-points"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Good Boy Points
            </Link>
            <Link
              href="/photos"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Photos
            </Link>
            {/* Users link should always be at the bottom of navigation links (before profile links) */}
            <Link
              href="/users"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Users
            </Link>
            <Link
              href="/profile"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Your Profile
            </Link>
            <Link
              href="/settings"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Settings
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}); 